import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'

// UI
import { Box, Button, Flex, Heading, HStack, Spacer } from '@chakra-ui/react'
import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'

import { NonFormUpload, LoadingSection } from '@/components/Elements'

import FullTable from '@/components/Table/FullTable/FullTable'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
//helper
import useUser from '@/features/users/useUser'
import { createColumnHelper } from '@tanstack/react-table'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createExcelBlob } from '@/utils/fileHelper'
import getFrameAgreementTableColumns from '../constants/frameAgreementTableColumns'
import useFrameAgreement from '../useFrameAgreement'
const FrameAgreementListPage = () => {
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:frameAgreement`, {})

  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const {
    useFetchAllFrameAgreements,
    useCreateFrameAgreement,
    useRemoveFrameAgreement,
    useUpdateFrameAgreement,
    useUploadFrameAgreements,
  } = useFrameAgreement()

  const { data: frameAgreementsResponse, isLoading, refetch: refetchFrameAgreement } = useFetchAllFrameAgreements(query)

  const { data: allFrameAgreementsResponse } = useFetchAllFrameAgreements({
    page: 1,
    pageSize: 0,
  })
  const removeFrameAgreement = useRemoveFrameAgreement()
  const updateFrameAgreement = useUpdateFrameAgreement()
  const uploadFrameAgreements = useUploadFrameAgreements()

  console.log('frameAgreementsResponse', frameAgreementsResponse)
  // Handler

  const handleDetail = (row) => {
    const frameAgreementId = row?.original?._id
    navigate(`/admin/frameAgreements/${frameAgreementId}`)
  }

  const handleDeleteFrameAgreement = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await removeFrameAgreement.mutateAsync(id)
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeFrameAgreement]
  )

  const handleActivateFrameAgreement = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateFrameAgreement.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateFrameAgreement]
  )

  const handleUpload = (file) => {
    if (!file) {
      toast.error('No File Uploaded')
      return
    }
    dispatch(showLoadingOverlay())
    uploadFrameAgreements.mutate(file, {
      onSettled: () => {
        dispatch(hideLoadingOverlay())
      },
    })
  }

  const handleDownload = useCallback(() => {
    try {
      if (!Array.isArray(allFrameAgreementsResponse?.data?.results)) {
        return
      }
      const excelData = allFrameAgreementsResponse?.data?.results.map((frameAgreement) => {
        return {
          profitCenterCode: frameAgreement.profitCenterCode,
          code: frameAgreement.code,
          name: frameAgreement.name,
          description: frameAgreement.description,
          quotationCode: frameAgreement.quotationCode,
          defaultProcurementOfficerEmail: frameAgreement?.defaultProcurementOfficerEmail || '',
          defaultTeamAssistantEmail: frameAgreement?.defaultTeamAssistantEmail || '',
          type: frameAgreement?.type || '',
          status: frameAgreement?.status || '',
        }
      })

      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'E-Approval Cost Center.xlsx')
    } catch (err) {
      console.log(err)
    }
  }, [allFrameAgreementsResponse?.data])

  const defaultColumns = getFrameAgreementTableColumns({
    handleActivateFrameAgreement,
    handleDeleteFrameAgreement,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <Flex alignItems="center" p={5} borderBottom="1px solid #d8dbe0" gap="3">
        <Heading>Frame Agreement</Heading>

        <Flex direction={'column'} gap={2}>
          <HStack>
            <Button colorScheme="gray" onClick={handleDownload}>
              Download
            </Button>
            <NonFormUpload uploadHandler={handleUpload} />
          </HStack>
        </Flex>

        <Spacer />
        <Link to="/admin/frameAgreements/create">
          <Button colorScheme="blue">Create Frame Agreement</Button>
        </Link>
      </Flex>
      <Box p={5}>
        <FullTable
          data={Array.isArray(frameAgreementsResponse?.data?.results) ? frameAgreementsResponse.data.results : []}
          rowCount={frameAgreementsResponse?.count}
          columnDef={defaultColumns}
          state={storedTableState}
          setState={setStoredTableState}
          handleRowClick={handleDetail}
        />
      </Box>
    </Box>
  )
}

export default FrameAgreementListPage
